import { normalizeTs, TAIPEI_TIME_ZONE } from '../lib/time';

const toTaipeiParts = (ts) => {
  const d = ts instanceof Date ? ts : new Date(normalizeTs(ts));
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TAIPEI_TIME_ZONE, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).formatToParts(d);
  const hh = Number(parts.find(p => p.type === 'hour')?.value || '0');
  const mm = Number(parts.find(p => p.type === 'minute')?.value || '0');
  const ss = Number(parts.find(p => p.type === 'second')?.value || '0');
  return { hh, mm, ss };
};

const isoDateTaipei = (ts) => {
  const d = ts instanceof Date ? ts : new Date(normalizeTs(ts));
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TAIPEI_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(d);
};

const hasExplicitTZ = (ts) => typeof ts === 'string' ? (ts.includes('Z') || /[+-]\d{2}:?\d{2}/.test(ts)) : (ts instanceof Date ? true : false);

const timestampToMinutesTaipei = (ts) => {
  const d = ts instanceof Date ? ts : new Date(normalizeTs(ts));
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TAIPEI_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d);
  const hh = Number(parts.find(p => p.type === 'hour')?.value || '0');
  const mm = Number(parts.find(p => p.type === 'minute')?.value || '0');
  return hh * 60 + mm;
};

const hmToMinutes = (hm) => {
  if (!hm || typeof hm !== 'string' || !hm.includes(':')) return null;
  const s = hm.trim();
  const ampmMatch = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampmMatch) {
    let h = Number(ampmMatch[1]);
    const m = Number(ampmMatch[2]);
    const ap = ampmMatch[3].toUpperCase();
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (ap === 'AM') {
      if (h === 12) h = 0; // 12:xx AM → 00:xx
    } else {
      if (h !== 12) h = h + 12; // PM add 12 except 12 PM
    }
    return h * 60 + m;
  }
  // Fallback 24-hour HH:MM
  const parts = s.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

export const computeAttendanceStatus = (timestamp, scheduleStartHM, scheduleEndHM, type, graceMinutes = 0) => {
  const normalizeType = (t) => {
    if (!t) return '';
    const s = String(t).toLowerCase().trim();
    if (s === 'time_in' || s === 'time in' || s === 'in') return 'time_in';
    if (s === 'time_out' || s === 'time out' || s === 'out') return 'time_out';
    return s.replace(/\s+/g, '_');
  };
  const actualMinutes = timestampToMinutesTaipei(timestamp);
  const start = hmToMinutes(scheduleStartHM);
  const end = hmToMinutes(scheduleEndHM);
  let status = 'on_time';
  let lateMinutes = 0;
  let undertimeMinutes = 0;

  const normType = normalizeType(type);
  if (normType === 'time_in' && start != null) {
    if (actualMinutes > start) {
      lateMinutes = actualMinutes - start;
      status = 'late';
    } else {
      status = 'on_time';
    }
  }

  if (normType === 'time_out' && end != null) {
    if (actualMinutes < end) {
      undertimeMinutes = end - actualMinutes;
      status = 'undertime';
    } else {
      status = 'on_time';
    }
  }

  const debug = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
  if (debug) {
    const ah = String(Math.floor(actualMinutes / 60)).padStart(2, '0');
    const am = String(actualMinutes % 60).padStart(2, '0');
    const actualHM = `${ah}:${am}`;
    try {
      const tzFlag = hasExplicitTZ(timestamp) ? 'explicitTZ' : 'naiveLocalTaipei';
      const dateISO = isoDateTaipei(timestamp);
      console.debug(`[AttendanceRules] ${tzFlag} date=${dateISO} type=${type} actual=${actualHM} start=${scheduleStartHM || 'N/A'} end=${scheduleEndHM || 'N/A'} grace=${graceMinutes} => status=${status}, late=${lateMinutes}, undertime=${undertimeMinutes}`);
    } catch {}
  }
  return { status, lateMinutes, undertimeMinutes };
};

export const computeDailyAttendanceSummary = ({
  timeInTs,
  timeOutTs,
  scheduleStartHM,
  scheduleEndHM,
  graceMinutes = 0,
  roundingStep = 1,
  roundingMode = 'none', // 'none' | 'up' | 'down' | 'nearest'
  breakMinutes = 0,
  approvedLeaveMinutes = 0
}) => {
  const round = (mins) => {
    if (roundingMode === 'none' || roundingStep <= 1) return mins;
    const step = Math.max(1, roundingStep);
    if (roundingMode === 'up') return Math.ceil(mins / step) * step;
    if (roundingMode === 'down') return Math.floor(mins / step) * step;
    return Math.round(mins / step) * step;
  };

  const toMinutes = (hm) => hmToMinutes(hm);
  const start = toMinutes(scheduleStartHM);
  const end = toMinutes(scheduleEndHM);

  const actualInMinutes = timeInTs ? timestampToMinutesTaipei(timeInTs) : null;
  const actualOutMinutes = timeOutTs ? timestampToMinutesTaipei(timeOutTs) : null;
  const dateInISO = timeInTs ? isoDateTaipei(timeInTs) : null;
  const dateOutISO = timeOutTs ? isoDateTaipei(timeOutTs) : null;
  const isCrossDay = Boolean(dateInISO && dateOutISO && dateInISO !== dateOutISO);
  
  // DEBUG LOGGING
  console.log('[computeDailyAttendanceSummary] Input:', {
    timeInTs,
    scheduleStartHM,
    actualInMinutes,
    start,
    dateInISO
  });

  let lateMinutes = 0;
  let undertimeMinutes = 0;
  let statusIn = 'on_time';
  let statusOut = 'on_time';

  if (actualInMinutes != null && start != null) {
    if (actualInMinutes > start) {
      lateMinutes = actualInMinutes - start;
      statusIn = 'late';
    } else {
      lateMinutes = 0;
      statusIn = 'on_time';
    }
  }
  if (!isCrossDay && actualOutMinutes != null && end != null) {
    if (actualOutMinutes < end) {
      undertimeMinutes = end - actualOutMinutes;
      statusOut = 'undertime';
    } else {
      undertimeMinutes = 0;
      statusOut = 'on_time';
    }
  } else if (isCrossDay && actualOutMinutes != null && end != null) {
    undertimeMinutes = 0;
    statusOut = 'on_time';
  }

  // Scheduled duration and actual duration
  const scheduledMinutes = (start != null && end != null && end >= start) ? (end - start) : null;
  let actualWorkedMinutes = null;
  if (actualInMinutes != null && actualOutMinutes != null && actualOutMinutes >= actualInMinutes) {
    actualWorkedMinutes = Math.max(0, actualOutMinutes - actualInMinutes - (breakMinutes || 0));
  }

  // Apply approved leave credit to reduce undertime
  if (approvedLeaveMinutes && undertimeMinutes) {
    undertimeMinutes = Math.max(0, undertimeMinutes - approvedLeaveMinutes);
  }

  // Rounding rules
  lateMinutes = round(lateMinutes);
  undertimeMinutes = round(undertimeMinutes);

  const explanation = {
    schedule: `${scheduleStartHM || 'N/A'}–${scheduleEndHM || 'N/A'}`,
    actual: `${actualInMinutes != null ? String(Math.floor(actualInMinutes/60)).padStart(2,'0') + ':' + String(actualInMinutes%60).padStart(2,'0') : 'N/A'}–${actualOutMinutes != null ? String(Math.floor(actualOutMinutes/60)).padStart(2,'0') + ':' + String(actualOutMinutes%60).padStart(2,'0') : 'N/A'}`,
    graceMinutes,
    rounding: { step: roundingStep, mode: roundingMode },
    breakMinutes,
    approvedLeaveMinutes,
    scheduledMinutes,
    actualWorkedMinutes,
    dates: { in: dateInISO, out: dateOutISO }
  };

  const debug = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
  if (debug) {
    try {
      console.debug(`[DailySummary] schedule=${explanation.schedule} actual=${explanation.actual} dates(in/out)=${dateInISO}/${dateOutISO} grace=${graceMinutes} round=${roundingMode}/${roundingStep} break=${breakMinutes} leave=${approvedLeaveMinutes} ⇒ late=${lateMinutes}, under=${undertimeMinutes}, worked=${actualWorkedMinutes}, scheduled=${scheduledMinutes}`);
    } catch {}
  }

  return { statusIn, statusOut, lateMinutes, undertimeMinutes, scheduledMinutes, actualWorkedMinutes, explanation };
};

export default { computeAttendanceStatus, computeDailyAttendanceSummary };
